import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Loader2,
  ArrowLeft,
  Edit,
  Building2,
  MapPin,
  Phone,
  Mail,
  User,
  Package,
  Calendar,
  Hash
} from 'lucide-react';
import { storeApiService, Store } from '../services/storeApi';

export function StoreViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchStore();
    }
  }, [id]);

  const fetchStore = async () => {
    try {
      setLoading(true);
      const storeData = await storeApiService.getStoreById(id!);
      setStore(storeData);
    } catch (error) {
      // console.error('Error fetching store:', error);
      toast.error('Failed to fetch store details');
      navigate('/stores');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/stores/edit/${id}`);
  };

  const handleBack = () => {
    navigate('/stores');
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      ACTIVE: { color: 'bg-green-100 text-green-800', text: 'Active' },
      INACTIVE: { color: 'bg-gray-100 text-gray-800', text: 'Inactive' },
      MAINTENANCE: { color: 'bg-yellow-100 text-yellow-800', text: 'Maintenance' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.INACTIVE;
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      RETAIL: { color: 'bg-blue-100 text-blue-800', text: 'Retail' },
      WAREHOUSE: { color: 'bg-purple-100 text-purple-800', text: 'Warehouse' },
      DISTRIBUTION_CENTER: { color: 'bg-orange-100 text-orange-800', text: 'Distribution Center' }
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.RETAIL;
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
            <p className="text-gray-500">Loading store details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Store not found</h3>
            <p className="text-gray-500 mb-4">The store you're looking for doesn't exist.</p>
            <Button onClick={handleBack} className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white">
              Back to Stores
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              onClick={handleBack}
              variant="outline"
              className="border-primary-200 text-primary-700 hover:bg-primary-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
                {store.storeName}
              </h1>
              <p className="text-gray-600 mt-1">Store Details</p>
            </div>
          </div>
          <Button
            onClick={handleEdit}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-200"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Store
          </Button>
        </div>

        {/* Store Information */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Basic Information */}
          <Card className="lg:col-span-2 border-primary-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
              <CardTitle className="flex items-center">
                <Building2 className="h-5 w-5 mr-2" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Hash className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Store Code</p>
                      <p className="font-medium text-gray-900">{store.storeCode}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Building2 className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Store Name</p>
                      <p className="font-medium text-gray-900">{store.storeName}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Package className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Store Type</p>
                      <div className="mt-1">
                        {getTypeBadge(store.storeType)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="h-5 w-5 flex items-center justify-center">
                      <div className={`w-3 h-3 rounded-full ${
                        store.status === 'ACTIVE' ? 'bg-green-500' : 
                        store.status === 'INACTIVE' ? 'bg-gray-500' : 'bg-yellow-500'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <div className="mt-1">
                        {getStatusBadge(store.status)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {store.capacity && (
                    <div className="flex items-center space-x-3">
                      <Package className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Capacity</p>
                        <p className="font-medium text-gray-900">{store.capacity.toLocaleString()}</p>
                      </div>
                    </div>
                  )}

                  {store.createdAt && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Created</p>
                        <p className="font-medium text-gray-900">
                          {new Date(store.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {store.updatedAt && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Last Updated</p>
                        <p className="font-medium text-gray-900">
                          {new Date(store.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {store.location && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Location</p>
                      <p className="text-gray-900 whitespace-pre-line">{store.location}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact & Additional Information */}
          <Card className="border-primary-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
              <CardTitle className="flex items-center">
                <User className="h-5 w-5 mr-2" />
                Contact & Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                {store.managerName && (
                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Manager</p>
                      <p className="font-medium text-gray-900">{store.managerName}</p>
                    </div>
                  </div>
                )}

                {store.phone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{store.phone}</p>
                    </div>
                  </div>
                )}

                {store.email && (
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{store.email}</p>
                    </div>
                  </div>
                )}

                {store.notes && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">Notes</p>
                    <p className="text-gray-900 text-sm leading-relaxed">{store.notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
